// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    ITheme,
    IIconStyles,
    mergeStyles,
    FontWeights,
    Theme, IStyle,
    memoizeFunction,
} from "@fluentui/react";
import { _pxToRem } from '../utils/cssUtils';
import { MessageThreadStyles } from "@azure/communication-react";
import { tokens } from "@fluentui/react-components";
import { pcpTokens } from "./themes/v9/pcpTokens";

/**
 * @private
 */
export const iconWrapperStyle = (theme: ITheme, isSubMenuOpen: boolean): IIconStyles => ({
  root: {
    margin: _pxToRem(3),
    // Show hover styles when the Edit/Delete menu is showing as this action button is still considered 'active'
    color: isSubMenuOpen ? theme.palette.black : theme.palette.neutralPrimary,
    strokeWidth: isSubMenuOpen ? _pxToRem(0.5) : _pxToRem(0),
    stroke: theme.palette.black,
    ':hover, :focus': {
      color: theme.palette.black,
      strokeWidth: _pxToRem(0.5)
    }
  }
});


const chatMessageCaretStyles = (position: 'left' | 'right') => {
    const horizontalPosition: IStyle = position === 'left' ? { left: "-12px" } : { right: "-12px" };

    return ({
        content: '""',
        width: "0px",
        height: "0px",
        borderStyle: "solid",
        borderWidth: "0 8px 8px 8px",
        borderColor: "transparent transparent #1088F9 transparent",
        transform: position === "left" ? "rotate(270deg)" : "rotate(90deg)",
        position: "absolute",
        ...horizontalPosition,
        top: "25%",
    });
};

export const chatMessageFooterStyles = memoizeFunction((isShortScreen: boolean) => ({
    root: {
        padding: isShortScreen ? '10px 18px' : '20px 18px',
        background: '#F9FAFB',
        position: 'relative',   //makes the 'box-shadow' css rule work
        boxShadow: '0px -2px 10px 0px rgba(0, 0, 0, 0.15)',
    } as IStyle
}));

const getMessageTopTextStyles = memoizeFunction((fontWeight: number) => ({
        color: 'white',
        fontFamily: 'Roboto',
        fontWeight,
        fontSize: '10px',
        lineHeight: '16px',
        letterSpacing: '4%',
    }));

export const messageThreadStyles: MessageThreadStyles = {
    newMessageButtonContainer: {
        left: '50%',
        transform: 'translate(-50%, -0%)',
        right: 'unset',
        bottom: '12px',
    },

    chatMessageContainer: {
        backgroundColor: pcpTokens.pcpColorNeutralBackground3,
        color: 'white',

        '&::before': {
            ...chatMessageCaretStyles('left'),
            borderColor: `transparent transparent ${pcpTokens.pcpColorNeutralBackground3} transparent`,
        },
    },
    myChatMessageContainer: {
        background: `linear-gradient(270deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandBackground2} 39%)`,  //== 'linear-gradient(270deg, #1088F9 0%, #7673FB 39%)',
        color: 'white',

        '&::before': {
            ...chatMessageCaretStyles('right'),
            borderColor: `transparent transparent ${tokens.colorBrandBackground2} transparent`,
        },
    },
    chatContainer: {
        color: 'white',
        paddingRight: '1rem',
        
        backgroundColor: tokens.colorNeutralBackground1 // #F0F2F4
    },
    chatItemMessageContainer: {
        color: 'white',
    },


    myChatItemMessageContainer: {
        color: 'white',
    },
    loadPreviousMessagesButtonContainer: {
        color: 'white',
    },
    systemMessageContainer: {
        color: 'white',
    },


    root: {

        // Other others file attachment
        '.fui-ChatMessage__body .fui-Card': {
            backgroundColor: '#FFFFFF',
        },
        // My message file attachment
        '.fui-ChatMyMessage__body .fui-Card': {
            backgroundColor: '#FFFFFF',
            // backgroundColor: '#6f7d98',
        },

        // Hide the avatar.
        // After hiding, remove the marginLeft from the messages
        // to avoid whitespace to the left.
        '.fui-ChatMessage__avatar': {
            display: 'none',
        },
        '.fui-ChatMessage, .fui-ChatMessage__body': {
            marginLeft: '0',
        },

        '.fui-ChatMyMessage__timestamp, .fui-ChatMessage__timestamp': {
            '> span': getMessageTopTextStyles(400)
        },

        '.fui-ChatMyMessage__author, .fui-ChatMessage__author': {
            '> span': getMessageTopTextStyles(700)
        },
        '[data-ui-id="student-chat-message-top"] .fui-ChatMyMessage__author': {
            width: 'auto',
            height: 'auto',
            clip: 'auto',
            position: 'static',
        }
    },
};
